import instance from '../utils/axios';

export interface DesignBOMItem {
  id: number;
  design_no: string;
  company_id: number;
  material_type: string;
  material_name: string;
  material_id: number | null;
  garment_part: string;
  consumption_per_piece: number;
  uom: string;
  wastage_pct: number;
  estimated_rate: number | null;
  size_adjustments: Record<string, number> | null;
  notes: string | null;
  is_optional: boolean;
  created_by: string | null;
  finalized_by: string | null;
  finalized_at: string | null;
  created_at: string;
  updated_at: string;
  material?: {
    id: number;
    material_code: string;
    material_type: string;
    available_qty: number;
    uom: string;
    rate: number;
    image_url: string | null;
  } | null;
}

export interface CreateBOMItemDTO {
  material_type: string;
  material_name: string;
  material_id?: number | null;
  garment_part: string;
  consumption_per_piece: number;
  uom: string;
  wastage_pct?: number;
  estimated_rate?: number | null;
  size_adjustments?: Record<string, number> | null;
  notes?: string;
  is_optional?: boolean;
}

export interface MaterialRequirement {
  bom_id: number;
  material_type: string;
  material_name: string;
  garment_part: string;
  uom: string;
  consumption_per_piece: number;
  wastage_pct: number;
  required_qty: number;
  estimated_cost: number;
  material_id: number | null;
  material_code: string | null;
  available_qty: number;
  is_sufficient: boolean;
  shortage: number;
  is_optional: boolean;
}

export interface CalculateResponse {
  design_no: string;
  quantities: Record<string, Record<string, number>>;
  requirements: MaterialRequirement[];
  summary: {
    total_estimated_cost: number;
    all_materials_available: boolean;
    shortage_count: number;
  };
}

export const designBomApi = {
  getDesignsWithBom: async (): Promise<string[]> => {
    const response = await instance.get('/design-bom/designs-with-bom');
    return response.data;
  },

  getByDesign: async (designNo: string): Promise<{ design: any; bomItems: DesignBOMItem[] }> => {
    const response = await instance.get(`/design-bom/${designNo}`);
    return response.data;
  },

  addItem: async (designNo: string, data: CreateBOMItemDTO): Promise<DesignBOMItem> => {
    const response = await instance.post(`/design-bom/${designNo}`, data);
    return response.data;
  },

  updateItem: async (designNo: string, id: number, data: Partial<CreateBOMItemDTO>): Promise<DesignBOMItem> => {
    const response = await instance.put(`/design-bom/${designNo}/${id}`, data);
    return response.data;
  },

  deleteItem: async (designNo: string, id: number): Promise<void> => {
    await instance.delete(`/design-bom/${designNo}/${id}`);
  },

  finalize: async (designNo: string): Promise<{ message: string; bomItems: DesignBOMItem[] }> => {
    const response = await instance.post(`/design-bom/${designNo}/finalize`);
    return response.data;
  },

  generateFromDesign: async (designNo: string): Promise<{ message: string; bomItems: DesignBOMItem[] }> => {
    const response = await instance.post(`/design-bom/${designNo}/generate-from-design`);
    return response.data;
  },

  calculate: async (designNo: string, quantities: Record<string, Record<string, number>>): Promise<CalculateResponse> => {
    const response = await instance.post(`/design-bom/${designNo}/calculate`, { quantities });
    return response.data;
  },
};
